// Module-level store behind the header's Notifications menu.
//
// Lives outside the component tree on purpose. AppHeader is rendered by
// every screen, so the menu remounts on each navigation; the old
// InstanceQueueIndicator kept its state (and its "already seen" sets for
// the bell) inside the component, which meant an invite arriving during
// a screen change never rang and every navigation re-ran the full fetch
// storm. Same pattern as achievementToasts / customMusicManager: one
// singleton, subscribers re-render from it.
//
// Two feeds:
//   matches   actionable rows derived LIVE from the instance system
//             (invites addressed to you, lobbies you are waiting in).
//             Their truth is the instance row, so they are refetched on
//             the server's per-user `matches:changed` push and never
//             cached across sessions.
//   activity  durable notifications from the server feed (deck comment,
//             chat message, mail, auction bid, ...). Arrive as
//             `notification:new`, replayed as `notifications:snapshot` on
//             every (re)connect so nothing is lost while offline.
import { on, off, onServerReconnect } from './serverClient';
import {
  listInstances,
  listInvitedInstances,
  subscribePublicInstancesChanged,
} from './arena/instancesApi';
import { getDraftInvites, getDraftMatchInvites } from './arena/draftApi';
import { fetchNotifications, markAllNotificationsRead, resolveNotification as apiResolve } from './notificationsApi';
import { isActiveQueueRow } from './arena/instanceVisibility';
import { UI, playUI, playVoice } from './arena/uiSounds';
import { notificationSoundEnabled } from './arena/soundSettings';
import { duckAmbience } from './arena/ambienceManager';
import { duckMusic } from './arena/musicManager';
import { duckCustomMusic } from './arena/customMusicManager';

// Safety net only. Every mutation the server makes to invites/lobbies
// pushes matches:changed, so this is for a missed push, not the primary
// refresh path the 30s poll used to be.
const SAFETY_POLL_MS = 5 * 60 * 1000;

// Duck envelope for the invite summons, tied to snd-ui-match-invite.mp3 (~6s).
const INVITE_DUCK = { holdMs: 1300 };

const state = {
  started: false,
  profileId: null,
  invites: [],      // unified invite rows (source: 'instance' | 'draft' | 'draft-match')
  instances: [],    // lobbies the user is waiting in
  activity: [],     // notifications, newest first
  unread: 0,        // unread activity count (server-authoritative)
  loading: true,
};

const listeners = new Set();
const unsubscribers = [];
let pollTimer = null;
let seenInviteIds = null;
let seenSeatCounts = null;
let lastRingAt = 0;
let openHandler = null;

function emit() {
  for (const cb of listeners) {
    try { cb(getState()); } catch (err) { console.error('[notifications] listener failed:', err); }
  }
}

export function getState() {
  return {
    invites: state.invites,
    instances: state.instances,
    activity: state.activity,
    unread: state.unread,
    loading: state.loading,
    matchCount: state.invites.length + state.instances.length,
  };
}

export function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

// App-level navigation for a clicked activity row (deep links). Registered
// once by app.jsx, same idea as instanceQueueBus.
export function registerNotificationOpenHandler(fn) {
  openHandler = fn || null;
}

export function openNotification(n) {
  if (openHandler) openHandler(n);
}

// ── Sounds ───────────────────────────────────────────────────────────

// The one place the invite summons plays. A match invite is someone sitting
// in a lobby waiting on an answer, so it ducks the beds; that separation is
// what makes it read as an event. Its room is baked into the sample, so it
// does not take the reverb bus on top.
export function ringBell() {
  const now = Date.now();
  if (now - lastRingAt < 2000) return;
  lastRingAt = now;
  playVoice(UI.MATCH_INVITE, { volume: 1.2 });
  for (const duck of [duckAmbience, duckMusic, duckCustomMusic]) duck(INVITE_DUCK);
}

// ── Matches feed ─────────────────────────────────────────────────────

function mapInstanceInvite(inst) {
  return {
    id: `inv-${inst.id}`,
    source: 'instance',
    instanceId: inst.id,
    senderId: inst.inviter_id,
    senderName: inst.inviter_name || inst.creator_name || null,
    senderAvatar: (inst.participants || []).find((p) => p.userId === inst.inviter_id)?.profileAvatar || null,
    type: 'match-invite',
    createdAt: inst.invited_at || inst.last_activity_at,
    metadata: {
      instanceId: inst.id,
      mode: inst.mode,
      format: inst.format || null,
      visibility: inst.visibility,
      rankedOptIn: !!inst.ranked_opt_in,
      resume: inst.invite_kind === 'resume',
    },
  };
}

function mapDraftInvite(ev) {
  const mode = ev.mode === 'sealed' ? 'sealed' : 'draft';
  return {
    id: `draft-${ev.id}`,
    source: 'draft',
    eventId: ev.id,
    senderId: ev.creatorId,
    senderName: ev.creatorName || null,
    senderAvatar: ev.creatorAvatar || null,
    type: 'draft-invite',
    createdAt: ev.invitedAt || ev.createdAt,
    metadata: {
      eventId: ev.id,
      mode,
      visibility: ev.visibility === 'public' ? 'public' : 'private',
      setKey: ev.setKey || null,
      podSize: ev.podSize || null,
      participantCount: Number.isFinite(ev.participantCount) ? ev.participantCount : null,
      entryCost: ev.entryCost || 0,
      scheduledAt: ev.scheduledAt || null,
    },
  };
}

function mapDraftMatchInvite(inv) {
  return {
    id: `dm-${inv.matchId}`,
    source: 'draft-match',
    eventId: inv.eventId,
    matchId: inv.matchId,
    senderId: inv.opponentId,
    senderName: inv.opponentName || 'Opponent',
    senderAvatar: inv.opponentAvatar || null,
    type: 'draft-match-invite',
    createdAt: inv.invitedAt || Date.now(),
    metadata: {
      mode: inv.mode === 'sealed' ? 'sealed' : 'draft',
      round: inv.round,
      resume: !!inv.resume,
      eventId: inv.eventId,
      matchId: inv.matchId,
    },
  };
}

// The last seat filling in a lobby the user is waiting in is the other
// half of "a match is ready for you" and earns the same summons as an
// invite. Only the fill, not every arrival: a four-seat pod is not four
// summons. Only counts instances seen in the previous pass: a row appearing
// full is the user's own arrival, not news.
function noticeSeatFills(all) {
  const counts = new Map();
  for (const inst of all || []) {
    if (inst.phase === 'completed' || inst.phase === 'abandoned') continue;
    counts.set(inst.id, { n: (inst.participants || []).length, max: inst.seats_max });
  }
  const seen = seenSeatCounts;
  seenSeatCounts = counts;
  if (!seen) return false;
  for (const [id, { n, max }] of counts) {
    const before = seen.get(id);
    if (before !== undefined && n > before.n && max && n >= max) return true;
  }
  return false;
}

let matchesRefreshInFlight = null;
export async function refreshMatches() {
  if (!state.started) return;
  if (matchesRefreshInFlight) return matchesRefreshInFlight;
  matchesRefreshInFlight = (async () => {
    const settle = (p) => p.then((v) => ({ ok: true, v }), () => ({ ok: false }));
    const [all, invited, draftInvites, matchInvites] = await Promise.all([
      settle(listInstances()),
      settle(listInvitedInstances()),
      settle(getDraftInvites()),
      settle(getDraftMatchInvites()),
    ]);
    let ring = false;
    if (all.ok) {
      state.instances = (all.v || []).filter((i) => isActiveQueueRow(i, state.profileId));
      if (noticeSeatFills(all.v)) ring = true;
    }
    // A failed fetch keeps the previous rows for that source rather than
    // blanking them: the old widget replaced the whole array on every
    // pass, so a flaky connection made invites flicker in and out.
    const prev = state.invites;
    const keep = (source) => prev.filter((i) => i.source === source);
    const invites = [
      ...(matchInvites.ok ? (matchInvites.v || []).map(mapDraftMatchInvite) : keep('draft-match')),
      ...(invited.ok ? (invited.v || []).map(mapInstanceInvite) : keep('instance')),
      ...(draftInvites.ok ? (draftInvites.v || []).map(mapDraftInvite) : keep('draft')),
    ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    // Ring only for ids not in the previous live set. The first pass after
    // start only primes: invites that predate this session are not news.
    const seen = seenInviteIds;
    seenInviteIds = new Set(invites.map((i) => i.id));
    if (seen && invites.some((i) => !seen.has(i.id))) ring = true;

    state.invites = invites;
    if (ring) ringBell();
    emit();
  })().finally(() => { matchesRefreshInFlight = null; });
  return matchesRefreshInFlight;
}

// Optimistic local drop after accept/decline; the server push that
// follows re-derives the truth.
export function removeInviteLocally(id) {
  state.invites = state.invites.filter((i) => i.id !== id);
  emit();
}

// ── Activity feed ────────────────────────────────────────────────────

function upsertActivity(n) {
  const rest = state.activity.filter((a) => a.id !== n.id);
  state.activity = [n, ...rest].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
}

function recount() {
  state.unread = state.activity.filter((a) => !a.readAt && !a.resolvedAt).length;
}

export async function refreshActivity() {
  if (!state.started) return;
  try {
    const { items, unread } = await fetchNotifications();
    state.activity = items;
    state.unread = unread;
    emit();
  } catch {
    // Pre-auth 401. Silent.
  }
}

// Opening the panel clears the badge server-side (read_at) but the rows
// keep their unread styling locally until the panel closes, so the eye can
// still find what is new.
export async function markAllRead() {
  const hadUnread = state.activity.some((a) => !a.readAt && !a.resolvedAt);
  if (!hadUnread) return;
  const now = new Date().toISOString();
  state.activity = state.activity.map((a) => (a.readAt ? a : { ...a, readAt: now }));
  state.unread = 0;
  emit();
  markAllNotificationsRead().catch(() => {});
}

export async function resolveNotification(id) {
  const now = new Date().toISOString();
  state.activity = state.activity.map((a) => (a.id === id ? { ...a, resolvedAt: now, readAt: a.readAt || now } : a));
  recount();
  emit();
  apiResolve(id).catch(() => {});
}

// ── Lifecycle ────────────────────────────────────────────────────────

export function startNotifications(profileId) {
  if (state.started && state.profileId === profileId) return;
  stopNotifications();
  state.started = true;
  state.profileId = profileId;
  state.loading = true;

  const onMatchesChanged = () => refreshMatches();
  // matches:changed is the per-user push every instance / invitation
  // mutation fires (sweeper included). The rest are belt-and-braces for
  // older server builds and the public-list broadcast.
  for (const type of ['matches:changed', 'instanceInvites:changed', 'instance:event']) {
    on(type, onMatchesChanged);
    unsubscribers.push(() => off(type, onMatchesChanged));
  }
  unsubscribers.push(subscribePublicInstancesChanged(onMatchesChanged));
  window.addEventListener('valkenhall:instances-changed', onMatchesChanged);
  unsubscribers.push(() => window.removeEventListener('valkenhall:instances-changed', onMatchesChanged));

  const onSnapshot = (data) => {
    state.activity = data?.items || [];
    state.unread = Number(data?.unread) || 0;
    state.loading = false;
    emit();
  };
  const onNew = (n) => {
    if (!n?.id) return;
    upsertActivity(n);
    recount();
    emit();
    // The soft social ping, subject to the player's notification-sound
    // toggles. The summons is reserved for match events. A direct message
    // is chimed by ChatPanel off the chat:message push (#66) and mail by
    // the new-mail path, so those feed rows stay silent here.
    if (n.kind !== 'chat.message' && n.kind !== 'mail.received' && notificationSoundEnabled(n.kind)) playUI(UI.NOTIFICATION_SOFT);
    for (const cb of newListeners) {
      try { cb(n); } catch {}
    }
  };
  const onRead = (data) => {
    const ids = new Set((data?.ids || []).map(String));
    if (!ids.size) return;
    const now = new Date().toISOString();
    state.activity = state.activity.map((a) => (ids.has(a.id) && !a.readAt ? { ...a, readAt: now } : a));
    recount();
    emit();
  };
  const onResolved = (data) => {
    const ids = new Set((data?.ids || []).map(String));
    if (!ids.size) return;
    const now = new Date().toISOString();
    state.activity = state.activity.map((a) => (ids.has(a.id) ? { ...a, resolvedAt: a.resolvedAt || now, readAt: a.readAt || now } : a));
    recount();
    emit();
  };
  on('notifications:snapshot', onSnapshot);
  on('notification:new', onNew);
  on('notification:read', onRead);
  on('notification:resolved', onResolved);
  unsubscribers.push(() => off('notifications:snapshot', onSnapshot));
  unsubscribers.push(() => off('notification:new', onNew));
  unsubscribers.push(() => off('notification:read', onRead));
  unsubscribers.push(() => off('notification:resolved', onResolved));

  // The server replays the snapshot and nudges matches:changed on every
  // connect; a reconnect refetch here covers a server that predates that.
  unsubscribers.push(onServerReconnect(() => { refreshMatches(); refreshActivity(); }));

  pollTimer = setInterval(() => refreshMatches(), SAFETY_POLL_MS);

  Promise.all([refreshMatches(), refreshActivity()]).finally(() => {
    state.loading = false;
    emit();
  });
}

export function stopNotifications() {
  for (const u of unsubscribers.splice(0)) { try { u(); } catch {} }
  clearInterval(pollTimer);
  pollTimer = null;
  state.started = false;
  state.profileId = null;
  state.invites = [];
  state.instances = [];
  state.activity = [];
  state.unread = 0;
  seenInviteIds = null;
  seenSeatCounts = null;
  emit();
}

// Live "a notification just arrived" hook for the toast layer.
const newListeners = new Set();
export function onNewNotification(cb) {
  newListeners.add(cb);
  return () => newListeners.delete(cb);
}
